/**
 * Context menus when editing and working with proteins.
 */
import * as analytics from 'analytics/Analytics';
import * as asserts from 'Asserts';
import {MolbioFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import * as BioUtil from 'biojs/Util';
import * as contextmenu from 'components/ContextMenu';
import {ANALYTICS_MODULE} from 'constants/AnalyticsConstants';
import PolymerMapTabView from 'molbio/maps/PolymerMapTabView';
import Protein from 'molbio/proteins/Protein';
import ProteinActions from 'molbio/proteins/ProteinActions';
import ProteinAnnotationEditorActions from 'molbio/proteins/ProteinAnnotationEditorActions';
import {ProteinAnnotationView} from 'molbio/proteins/ProteinAnnotationsView';
import ProteinStore from 'molbio/proteins/ProteinStore';
import PolymerMapTabState from 'molbio/tabstates/PolymerMapTabState';
import {BENCHLING_BLAST_MENU_TITLE} from 'search/blast/BLASTConstants';
import {runBLASTModalForSelection} from 'search/blast/BLASTUtil';
import {validateBLASTSequence} from 'search/blast/validation';
import {Operator} from 'search/SearchConstants';
import {BasicEvent} from 'util/Types';

export const openContextMenu = (
  e: BasicEvent | Event,
  protein: Protein,
  proteinMapView: PolymerMapTabView<PolymerMapTabState<Protein>>
): void => {
  contextmenu.view.open(e, [
    {
      name: 'Insert residues',
      click() {
        proteinMapView.showInputBubble({});
      },
      disabled() {
        return !protein.allowResidueEdits() || !protein.canInsertAt(protein.selection().start());
      },
      hide() {
        return !isFeatureEnabled({feature: MolbioFeature.MOLBIO_EDIT});
      },
    },
  ]);
};

export const openSelectionContextMenu = (e: BasicEvent | Event, protein: Protein): void => {
  const selectedAminoAcids = protein.residueSlice(protein.selection().start(), protein.selection().end()!);
  const hasNonStandardAminoAcids = BioUtil.containsNonStandardAminoAcids(selectedAminoAcids);
  contextmenu.view.open(e, [
    {
      name: 'Delete residues',
      click() {
        protein.deleteSelection();
      },
      disabled() {
        return !protein.allowResidueEdits() || !protein.isSelectionDeletable();
      },
      hide() {
        return !isFeatureEnabled({feature: MolbioFeature.MOLBIO_EDIT});
      },
    },
    {
      name: 'Create annotation',
      click() {
        analytics.track('protein', 'open_annotation_panel', {
          product: ANALYTICS_MODULE.MOLBIO,
          openedFrom: 'context_menu',
          protein_id: protein.get('id'),
        });
        ProteinAnnotationEditorActions.newAnnotation(protein.id);
        protein.fileState().get('toolbar').trigger('openProteinAnnotationsPanel');
      },
      disabled() {
        return !protein.allowFeatureEdits();
      },
      hide() {
        return !isFeatureEnabled({feature: MolbioFeature.MOLBIO_EDIT});
      },
      dataTestAttrs: {component: 'ContextMenu', element: 'createAnnotation'},
    },
    {
      name: 'Copy',
      copy() {
        return selectedAminoAcids;
      },
    },
    {
      name: hasNonStandardAminoAcids
        ? 'Back translate (cannot back-translate non-standard AAs)'
        : 'Back translate',
      click() {
        protein.handleBackTranslate();
      },
      disabled() {
        return hasNonStandardAminoAcids;
      },
      hide() {
        return !isFeatureEnabled({feature: MolbioFeature.MOLBIO_CREATE});
      },
    },
    {
      name: BENCHLING_BLAST_MENU_TITLE,
      async click() {
        await runBLASTModalForSelection(protein.selection());
      },
      disabled() {
        return (
          protein.selection().isEmpty() ||
          !validateBLASTSequence(protein.selection().getSelectionResidues(), Operator.BLASTP)
        );
      },
      hide() {
        return protein.selection().isEmpty() || !BenchlingConfig.ENABLE_BLAST_SEARCH;
      },
    },
  ]);
};

export const openAnnotationContextMenu = (
  e: BasicEvent | Event,
  protein: Protein,
  annotation: ProteinAnnotationView
): void => {
  asserts.assertAndContinue(
    ProteinStore.getSelectedAnnotationId(protein.id) === annotation.id,
    'Expected ProteinStore to be up to date before the context menu code runs.'
  );
  contextmenu.view.open(e, [
    {
      name: 'Delete annotation',
      click() {
        ProteinAnnotationEditorActions.delete(protein.id, annotation.id);
      },
      disabled() {
        return !protein.allowFeatureEdits();
      },
      hide() {
        return !isFeatureEnabled({feature: MolbioFeature.MOLBIO_EDIT});
      },
    },
    {
      name: 'Add to feature library',
      click() {
        ProteinActions.promptAddAnnotationToFeatureLibrary(protein.id, annotation.id);
      },
      hide() {
        return !isFeatureEnabled({feature: MolbioFeature.MOLBIO_ADMIN});
      },
    },
  ]);
};
